import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { WINDOW_SIZES } from 'src/constants/main';
import { appStore } from 'src/mobx/appStore';
import { cartStore } from 'src/mobx/cartStore';
import { IMealComboGroup, IProductInsideCombo } from 'src/types/combo';
import { IProduct, IProductSize } from 'src/types/products';
import { isCorrectProductSize, isSameComboItemsSizeGroup } from 'src/utils/helpers';
import ResponsiveContent from 'src/view/components/ResponsiveContent/ResponsiveContent';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import MealComboGroup from '../../components/MealComboGroup/MealComboGroup';
import ProductNotes from '../../components/ProductNotes/ProdutcNotes';
import MealComboItemsModals from '../MealComboItemsModals/MealComboItemsModals';
import './style.scss';

export interface ICurrentMealGroup extends IMealComboGroup {
  items: IProductInsideCombo[];
}

interface IProps {
  product?: IProduct;
  selectItem: (item: IProductInsideCombo) => void;
  onNotesChanged: (notes: string) => void;
}

const MealComboItemsBlock: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const getCurrentSizeGroups = useCallback((): ICurrentMealGroup[] => {
    const { product } = props;
    if (!product?.groups?.length || !product.items?.length) return [];

    const findGroupItems = (groupId: number): IProductInsideCombo[] => {
      const sizeId: number = isCorrectProductSize(product.size) ? (product.size as IProductSize)?.id : 0;
      return product.items?.filter((item) => isSameComboItemsSizeGroup(item, undefined, { sizeId, groupId })) || [];
    };

    const groupFormat = (group: IMealComboGroup): ICurrentMealGroup | null => {
      const currentGroupItems: IProductInsideCombo[] = findGroupItems(group.id);
      if (!currentGroupItems.length) return null;

      return { ...group, items: currentGroupItems };
    };

    return product.groups.reduce(
      (sum: ICurrentMealGroup[], current: IMealComboGroup) => sum.concat(groupFormat(current) || []),
      []
    );
  }, [props.product?.size, props.product?.groups, props.product?.items]);

  const currentGroups: ICurrentMealGroup[] = getCurrentSizeGroups();
  const comboSize: IProductSize | null =
    props.product?.size && isCorrectProductSize(props.product.size) ? (props.product?.size as IProductSize) : null;
  const showNotes =
    appStore.settings?.item_notes &&
    (!cartStore.isSMS || (cartStore.isEditable && props.product?.hash) || props.product?.notes) &&
    props.product?.type;

  return (
    <>
      <ResponsiveContent min={WINDOW_SIZES.md}>
        <ScrollableBlock
          showScrollbar
          scrollbarSize="small"
          className="full-height pxmr-2"
          scrollClassName="meal-combo-items-block__scroll full-height d-flex flex-column"
        >
          {currentGroups.map((group, index) => (
            <MealComboGroup
              key={group.id + '-meal-group'}
              index={index}
              group={group}
              comboSize={comboSize}
              selectItem={props.selectItem}
            />
          ))}
        </ScrollableBlock>
      </ResponsiveContent>

      <ResponsiveContent max={WINDOW_SIZES.md}>
        <div className="pxpx-3">
          <MealComboItemsModals product={props.product} groups={currentGroups} selectItem={props.selectItem} />
          {showNotes ? (
            <ProductNotes
              loading={!props.product}
              onChange={props.onNotesChanged}
              value={props.product?.notes || ''}
              disabled={!cartStore.isEditable}
            />
          ) : null}
        </div>
      </ResponsiveContent>
    </>
  );
});

export default MealComboItemsBlock;
